import React, { FC, useEffect, useState } from 'react';
import { useLazyQuery, NetworkStatus } from '@apollo/client';
import { map, some, get } from 'lodash';
import { t, Trans } from '@lingui/macro';
import { SideSheetContentCol, Box, Icon, Text, Heading, Spinner, SpinnerSize } from '@bechtel/gds';
import {
  Block,
  BlockList,
  PagingInput,
  BlockFilterInput,
  BlockSortByTerm,
  BlockSearchByTerm,
  BlockStatusCode,
} from '@bechtel/types';
import { ErrorRetry } from '@bechtel/client-common';
import { GET_USER_BLOCKS } from '@bechtel/client-gql';
import { BlockStyled } from '../styled';
import { BlockTypeMessages, BlockTypeDescriptionMessages, SingleBlock } from '~/components';
import { MultiBlockSideSheetContentColStyled } from '../../styled';
import { MultiBlockProps } from './types';
import { ResolveSubHeadingStyled } from '~/components/organisms/resolve-single-block/styled';

interface ResolveMultiSubHeadingProps {
  heading: string;
}

export const ResolveMultiSubHeading: FC<ResolveMultiSubHeadingProps> = ({ heading }) => {
  return (
    <ResolveSubHeadingStyled>
      <Heading family="graphik" size="400" weight="600" color="grey.1000">
        {heading}
      </Heading>
    </ResolveSubHeadingStyled>
  );
};

export const MultiBlock: FC<MultiBlockProps> = ({
  workPackageTag,
  resolvedBlocks,
  onSelectBlock,
  assignedCrewMembers,
  weldCommodityAssignees,
  facilities,
  blockList,
}) => {
  const milestoneBlocks = useState<Block[] | undefined>(blockList)[0];
  const blocksLimit = 10;
  const [getBlocks, { loading, error, data, refetch, networkStatus, fetchMore }] = useLazyQuery<
    { blocks: BlockList },
    {
      paging: PagingInput;
      input: BlockFilterInput;
    }
  >(GET_USER_BLOCKS, {
    variables: {
      paging: {
        cursor: '0',
        limit: blocksLimit,
      },
      input: {
        searchKeyword: workPackageTag,
        sortBy: BlockSortByTerm.MOST_RECENT_UPDATE,
        assignedToMe: false,
        searchBy: BlockSearchByTerm.WP_NUMBER,
        filterByBlockStatuses: [BlockStatusCode.BLOCK_OPENED],
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });
  const blocks: BlockList['items'] =
    milestoneBlocks && milestoneBlocks?.length > 0 ? milestoneBlocks : get(data, 'blocks.items', []);
  const totalPages = Math.ceil(get(data, 'blocks.total', 1) / blocksLimit);
  const blockListCursor = get(data, 'blocks.cursor', 0);

  useEffect(() => {
    if (milestoneBlocks && milestoneBlocks.length > 0) return;
    getBlocks();
  }, [milestoneBlocks, getBlocks]);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      blockListCursor <= totalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: blockListCursor.toString(),
            limit: blocksLimit,
          },
        },
      });
    }
  };
  const [selectedBlock, selectBlock] = useState<Block>();
  return (
    <>
      <MultiBlockSideSheetContentColStyled
        onScroll={(e) => {
          handleScroll(e);
        }}
      >
        {/* <ResolveBlockHeading heading="Select a Block" /> */}
        {map(blocks, (blockItem: Block, index) => {
          const isBlockResolved = resolvedBlocks.length > 0 && some(resolvedBlocks, { id: blockItem.id });
          return (
            <BlockStyled
              data-testid={`multi-block-${blockItem.id}`}
              key={blockItem.id}
              selected={selectedBlock && selectedBlock.id === blockItem.id ? true : false}
              onClick={() => {
                selectBlock(blockItem);
                onSelectBlock(blockItem);
              }}
            >
              <Box flexDirection={{ sm: 'column' }}>
                <Box mb={{ sm: '450' }}>
                  <Text size="400" weight="600" family="graphik" color="grey.1000">
                    <Trans>Block</Trans> {index + 1}: <Trans id={BlockTypeMessages[blockItem.type.title].id}></Trans>
                  </Text>
                </Box>
                <Text color="grey.1000" size="400">
                  <Trans id={BlockTypeDescriptionMessages[blockItem.type.description].id}></Trans>
                </Text>
              </Box>
              <Box alignSelf={{ sm: 'flex-start' }}>
                <Icon fillColor="blue.800" icon={isBlockResolved ? 'check' : 'edit'} />
              </Box>
            </BlockStyled>
          );
        })}
        {(loading || networkStatus === NetworkStatus.refetch) && (
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
            data-testid="loading"
          >
            <Spinner size={SpinnerSize.LARGE} />
          </Box>
        )}
        {!loading && error && (
          <div data-testid="retry">
            <ErrorRetry refetch={refetch}>
              <Trans>There was a problem loading Blocks. Please click the button below to try again.</Trans>
            </ErrorRetry>
          </div>
        )}
      </MultiBlockSideSheetContentColStyled>
      <SideSheetContentCol>
        {selectedBlock && (
          <SingleBlock
            assignedCrewMembers={assignedCrewMembers}
            weldCommodityAssignees={weldCommodityAssignees}
            facilities={facilities}
            blockId={selectedBlock.id}
          />
        )}
        {!selectedBlock && (
          <>
            <ResolveMultiSubHeading heading={t`Block Details`} />
            <Box data-testid="no-block-selected" m={{ sm: '500' }}>
              <Text color="grey.1000" size="300">
                <Trans> Select a Block on the Left to View Details and Resolve.</Trans>
              </Text>
            </Box>
          </>
        )}
      </SideSheetContentCol>
    </>
  );
};
